import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ToastProvider";
import { listOrgMembers, updateOrgMember, type OrgMemberSummary } from "@/api/orgMembers";
import { useSessionUser } from "@/hooks/useSessionUser";

const ADMIN_ROLES = new Set(["owner", "admin"]);

export default function OrgMembersPanel() {
  const { role, organization } = useSessionUser();
  const { push } = useToast();
  const isAdmin = ADMIN_ROLES.has((role ?? "").toLowerCase());
  const [members, setMembers] = useState<OrgMemberSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!isAdmin || !organization?.id) return;
    setLoading(true);
    try {
      const payload = await listOrgMembers();
      setMembers(payload);
    } catch (err) {
      push({ title: "Team", message: err instanceof Error ? err.message : "Unable to load team members." });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, organization?.id, push]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      if (a.role === b.role) return a.name?.localeCompare(b.name ?? "") ?? 0;
      if (ADMIN_ROLES.has(a.role.toLowerCase())) return -1;
      if (ADMIN_ROLES.has(b.role.toLowerCase())) return 1;
      return a.role.localeCompare(b.role);
    });
  }, [members]);

  const toggleFinancials = async (member: OrgMemberSummary, nextValue: boolean) => {
    setSavingId(member.user_id);
    try {
      const updated = await updateOrgMember(member.user_id, { can_view_financials: nextValue });
      setMembers((prev) => prev.map((entry) => (entry.user_id === updated.user_id ? updated : entry)));
    } catch (err) {
      push({ title: "Permissions", message: err instanceof Error ? err.message : "Unable to update access." });
    } finally {
      setSavingId(null);
    }
  };

  if (!isAdmin) return null;

  return (
    <section className="aa-glass-card p-5 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Team members</h2>
          <p className="text-sm text-muted-foreground">
            Control who can view financial data. Owners and admins always have access.
          </p>
        </div>
        {organization?.name && (
          <div className="rounded-full border border-border/60 px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground">
            {organization.name}
          </div>
        )}
      </div>

      {!organization?.id && (
        <p className="text-sm text-rose-400">Organization context missing. Refresh the page to retry.</p>
      )}

      {organization?.id && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">Members</h3>
            {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>View financials</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedMembers.map((member) => {
                const memberRole = member.role?.toLowerCase() ?? "";
                const locked = ADMIN_ROLES.has(memberRole);
                return (
                  <TableRow key={member.user_id}>
                    <TableCell>{member.name ?? "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{member.email ?? "—"}</TableCell>
                    <TableCell className="capitalize">{member.role ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={locked ? true : member.can_view_financials}
                          disabled={locked || savingId === member.user_id}
                          onCheckedChange={(value) => void toggleFinancials(member, Boolean(value))}
                        />
                        <span className="text-xs text-muted-foreground">
                          {locked ? "Always on" : member.can_view_financials ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!sortedMembers.length && !loading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-sm text-muted-foreground">
                    No team members found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Button variant="outline" size="sm" onClick={() => void fetchMembers()} disabled={loading}>
        Refresh list
      </Button>
    </section>
  );
}
